import { Link, useLocation } from "@tanstack/react-router";
import { Facebook, Instagram, Linkedin, Twitter, Youtube } from "lucide-react";
import { navProducts as products, navStates as states } from "@/data/nav-catalog";
import { SITE } from "@/data/site";
import { productApplyHref } from "@/lib/utils";
import { EqualHousingLogo } from "@/components/site/EqualHousingLogo";
import { LicensingDisclosure } from "@/components/site/LicensingDisclosure";
import { PreferredSourceLink } from "@/components/site/PreferredSourceLink";
import { isPreferredSourceEligible } from "@/lib/page-eligibility";
import logoAsset from "@/assets/simply-approved-logo-stacked.png.asset.json";
import { COMPANY } from "@/config/company";
import { NMLS_CONSUMER_ACCESS_LABEL } from "@/lib/legal-disclosure";
import { BrandNetworkFooterList } from "@/components/site/BrandNetwork";

function TikTokIcon({ size = 20, ...props }: React.SVGProps<SVGSVGElement> & { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" {...props}>
      <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z" />
    </svg>
  );
}

const SOCIAL_LINKS = [
  { label: "Facebook", href: "https://www.facebook.com/simplyapprovedmortgages", icon: Facebook },
  { label: "Instagram", href: "https://www.instagram.com/simplyapprovedmortgages/", icon: Instagram },
  { label: "LinkedIn", href: "https://www.linkedin.com/company/simply-approved-mortgages", icon: Linkedin },
  { label: "X", href: "https://x.com/simplyapprovedm", icon: Twitter },
  { label: "YouTube", href: "https://www.youtube.com/channel/UCir0f56T6xVFcEtGv_LadtQ", icon: Youtube },
  { label: "TikTok", href: "https://www.tiktok.com/@simplyapprovedmortgages", icon: TikTokIcon },
];

const DSCR_PROGRAM_LINKS = [
  { to: "/how-dscr-works", label: "How DSCR Works" },
  { to: "/dscr-loan-requirements", label: "DSCR Requirements" },
  { to: "/dscr-loan-pros-and-cons", label: "DSCR Pros & Cons" },
  { to: "/dscr-cash-out-refinance", label: "DSCR Cash-Out Refinance" },
  { to: "/dscr-rate-term-refinance", label: "DSCR Rate & Term Refinance" },
  { to: "/interest-only-dscr-loans", label: "Interest-Only DSCR" },
  { to: "/no-ratio-dscr-loans", label: "No-Ratio DSCR" },
  { to: "/dscr-heloc", label: "Investment Property HELOC" },
  { to: "/dscr-second-lien-heloc", label: "Second-Lien DSCR HELOC" },
];

const TOOL_LINKS = [
  { to: "/dscr-calculator", label: "DSCR Calculator" },
  { to: "/rental-property-deal-analyzer", label: "Rental Property Deal Analyzer" },
  { to: "/brrrr-calculator", label: "BRRRR Calculator" },
  { to: "/cash-on-cash-return-calculator", label: "Cash-on-Cash Calculator" },
  { to: "/cap-rate-calculator", label: "Cap Rate Calculator" },
  { to: "/arv-calculator", label: "ARV Calculator" },
  { to: "/rent-needed-for-dscr", label: "Rent Needed for DSCR" },
  { to: "/dscr-payment-calculator", label: "DSCR Payment Calculator" },
  { to: "/ltv-equity-calculator", label: "LTV & Equity Calculator" },
  { to: "/closing-cost-calculator", label: "Closing Cost Calculator" },
  { to: "/cash-out-refinance-calculator", label: "Cash-Out Refinance Calculator" },
  { to: "/prepayment-penalty-calculator", label: "Prepayment Penalty Calculator" },
  { to: "/interest-only-vs-amortizing-calculator", label: "Interest-Only vs Amortizing" },
  { to: "/rates", label: "See Today's Rates" },
  { to: "/investment-property-rates", label: "Live Rate Center" },
  { to: "/dscr-loan-rates", label: "DSCR Loan Rates" },
  { to: "/dscr-heloc-rates", label: "DSCR HELOC Pricing" },
  { to: "/dscr-rate-history", label: "DSCR Rate History" },
];

const RESOURCE_LINKS = [
  { to: "/compare", label: "Loan Comparisons" },
  { to: "/best-rental-markets", label: "Best Rental Markets" },
  { to: "/tools/compare-cities", label: "Compare Investor Cities" },
  { to: "/section-8-investing-map", label: "Section 8 Investing Map" },
  { to: "/hud-fair-market-rents", label: "HUD Fair Market Rents" },
  { to: "/hud-income-limits", label: "HUD Income Limits" },
  { to: "/qualified-census-tracts", label: "Qualified Census Tracts" },
  { to: "/difficult-development-areas", label: "Difficult Development Areas" },
  { to: "/glossary", label: "Investor Glossary" },
];

export function Footer() {
  const topStates = states.filter((s) => ["florida", "colorado"].includes(s.slug));
  const loc = useLocation();
  const applyHref = productApplyHref(loc.pathname);
  const preferredSourceEligible = isPreferredSourceEligible(loc.pathname);
  return (
    <footer className="mt-24 border-t border-border bg-brand-dark text-primary-foreground">
      <div className="container mx-auto max-w-7xl px-4 py-16">
        <div className="grid gap-10 sm:grid-cols-2 lg:grid-cols-5">
          <div>
            <div className="rounded-md bg-primary-foreground/95 p-3 inline-block">
              <img src={logoAsset.url} alt="Simply Approved Mortgages" className="h-24 w-auto" width={1120} height={880} />
            </div>
            <p className="mt-4 font-display text-base italic text-brand-gold">{SITE.mission}.</p>
            <div className="mt-2 text-sm text-primary-foreground/80">
              DSCR & business-purpose lending for US real estate investors.
            </div>

            <div className="mt-4 space-y-1 text-sm">
              <a href={SITE.phoneHref} className="block hover:text-brand-gold">{SITE.phone}</a>
              <Link to="/contact" className="block hover:text-brand-gold">Contact us</Link>
              <a href="https://dscrloanmarket.com" className="block hover:text-brand-gold">dscrloanmarket.com</a>
            </div>
          </div>
          <div>
            <div className="text-sm font-semibold text-brand-gold">Programs</div>
            <ul className="mt-3 space-y-2 text-sm">
              {products.map((p) => (
                <li key={p.slug}>
                  <Link to={`/${p.slug}` as string} className="text-primary-foreground/80 hover:text-brand-gold">
                    {p.shortName}
                  </Link>
                </li>
              ))}
              <li>
                <Link to="/mid-term-rental-loans" className="text-primary-foreground/80 hover:text-brand-gold">Mid-Term Rental</Link>
              </li>
            </ul>

            <div className="mt-6 text-sm font-semibold text-brand-gold">DSCR Programs</div>
            <ul className="mt-3 space-y-2 text-sm">
              {DSCR_PROGRAM_LINKS.map((l) => (
                <li key={l.to}>
                  <Link to={l.to} className="text-primary-foreground/80 hover:text-brand-gold">{l.label}</Link>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <div className="text-sm font-semibold text-brand-gold">Calculators & Rates</div>
            <ul className="mt-3 space-y-2 text-sm">
              {TOOL_LINKS.map((l) => (
                <li key={l.to}>
                  <Link to={l.to} className="text-primary-foreground/80 hover:text-brand-gold">{l.label}</Link>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <div className="text-sm font-semibold text-brand-gold">Resources</div>
            {preferredSourceEligible && (
              <PreferredSourceLink
                surface="footer"
                className="mt-3 text-primary-foreground/80 hover:text-brand-gold"
              />
            )}
            <ul className="mt-3 space-y-2 text-sm">
              {RESOURCE_LINKS.map((l) => (
                <li key={l.to}>
                  <Link to={l.to} className="text-primary-foreground/80 hover:text-brand-gold">{l.label}</Link>
                </li>
              ))}
            </ul>

            <div className="mt-6 text-sm font-semibold text-brand-gold">Where We Lend</div>
            <ul className="mt-3 grid grid-cols-2 gap-x-3 gap-y-2 text-sm">
              {topStates.map((s) => (
                <li key={s.slug}>
                  <Link
                    to="/dscr-loans/$state"
                    params={{ state: s.slug }}
                    className="text-primary-foreground/80 hover:text-brand-gold"
                  >
                    {s.name}
                  </Link>
                </li>
              ))}
              <li className="col-span-2">
                <Link to="/where-we-lend" className="font-semibold text-brand-gold hover:underline">
                  See all lending states →
                </Link>
              </li>
            </ul>
          </div>
          <div>
            <div className="text-sm font-semibold text-brand-gold">Company</div>
            <ul className="mt-3 space-y-2 text-sm">
              <li><Link to="/about" className="text-primary-foreground/80 hover:text-brand-gold">About</Link></li>
              <li><Link to="/investor-journey" className="text-primary-foreground/80 hover:text-brand-gold">Investor Journey</Link></li>
              <li><Link to="/business-setup" className="text-primary-foreground/80 hover:text-brand-gold">Business Setup</Link></li>
              <li><Link to="/process" className="text-primary-foreground/80 hover:text-brand-gold">Loan Process</Link></li>
              <li><Link to="/blog" className="text-primary-foreground/80 hover:text-brand-gold">Blog</Link></li>
              <li><Link to="/partners" className="text-primary-foreground/80 hover:text-brand-gold">Partners</Link></li>
              <li><Link to="/faq" className="text-primary-foreground/80 hover:text-brand-gold">FAQ</Link></li>
              <li><Link to="/contact" className="text-primary-foreground/80 hover:text-brand-gold">Contact</Link></li>
              <li><Link to={applyHref} className="text-primary-foreground/80 hover:text-brand-gold">Start My Approval</Link></li>
            </ul>

            <div className="mt-6 text-sm font-semibold text-brand-gold">Simply Approved Network</div>
            <BrandNetworkFooterList />
          </div>
        </div>

        <div className="mt-12 space-y-4 border-t border-primary-foreground/15 pt-6 text-xs text-primary-foreground/60">
          <LicensingDisclosure headingClassName="text-brand-gold" collapsibleDetails locale={loc.pathname.startsWith("/es") ? "es" : "en"} />

          <nav aria-label="Legal" className="flex flex-wrap gap-x-5 gap-y-2">
            <Link to="/legal/privacy" className="hover:text-brand-gold">Privacy Policy</Link>
            <Link to="/legal/terms" className="hover:text-brand-gold">Terms &amp; Conditions</Link>
            <Link to="/legal/disclaimers" className="hover:text-brand-gold">Disclaimers</Link>
            <Link to="/legal/licensing" className="hover:text-brand-gold">Disclosures &amp; Licensing</Link>
            <Link to="/legal/editorial-standards" className="hover:text-brand-gold">Editorial standards</Link>
            <Link to="/legal/calculator-methodology" className="hover:text-brand-gold">Calculator methodology</Link>
            <Link to="/legal/accessibility" className="hover:text-brand-gold">Accessibility</Link>
            <a
              href={COMPANY.nmlsConsumerAccessUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-brand-gold"
            >
              NMLS Consumer Access: {NMLS_CONSUMER_ACCESS_LABEL}
            </a>
          </nav>

          <div className="flex flex-wrap items-center gap-4">
            <span className="text-sm font-medium text-primary-foreground/90">Follow us:</span>
            {SOCIAL_LINKS.map((s) => {
              const Icon = s.icon;
              return (
                <a
                  key={s.label}
                  href={s.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={`${SITE.brand} on ${s.label}`}
                  className="grid h-11 w-11 place-items-center rounded-md hover:text-brand-gold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold"
                >
                  <Icon size={20} />
                </a>
              );
            })}
          </div>

          <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
            <div className="flex items-center gap-3">
              <EqualHousingLogo className="text-primary-foreground/80" size={30} />
              <p>© {new Date().getFullYear()} Simply Approved Mortgages. NMLS #2620881. Equal Housing Opportunity.</p>
            </div>
            <p>Mortgage broker—not a direct lender. No government agency affiliation or endorsement.</p>
          </div>
        </div>

      </div>
    </footer>
  );
}
